import { Link, useMatch } from "react-router-dom";
import profile_placeholder from "../assets/images/profile_placeholder.png";
import React, { useState, useEffect, useContext } from "react";
import henceforthApi from "../utils/henceforthApi";
import moment from "moment";
import { GlobalContext } from "../context/Provider";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import henceofrthEnums from "../utils/henceofrthEnums";
import {
  Copy,
  Star,
  Car,
  Phone,
  Navigation,
  MicOff,
  FileText,
  RefreshCw,
  Clock,
  MapPin,
  ExternalLink,
  AlertTriangle,
  ArrowLeft,
  ChevronRight,
  ShieldCheck,
} from "lucide-react";
import { Button } from "../components/ui/button";

interface driver {
  _id?: string;
  name?: string;
  total_reviews?: number;
  email?: string;
  phone_no?: string | number;
  average_ratings?: number;
  profile_pic?: string;
}

interface user {
  _id?: string;
  email?: string;
  total_reviews?: number;
  profile_pic?: string;
  average_ratings?: number;
  name?: string;
  phone_no?: string | number;
  passenger_document?: string;
  passenger_selfie?: string;
}

interface booking {
  _id?: string;
  booking_id?: string;
  date?: string;
  pickup_time?: string;
  drop_off_time?: string;
  satus?: string;
  currency?: string;
  price?: number;
  total_price?: number;
  price_before_tax?: number;
  tax?: number;
  city_from?: string;
  city_to?: string;
  pickup?: { lat?: number; long?: number; address?: string };
  dropoff?: { lat?: number; long?: number; address?: string };
  vehicle_brand_name?: string;
  vehicle_model_name?: string;
  model_of_car?: string;
  vehicle_color_name?: string;
  licence_plate_number?: string;
  driver_detail?: driver;
  user_detail?: user;
  recordings?: any;
  refund_amount?: number;
  refund_percentage?: number;
  driver_cancellation_payout?: number;
  admin_cancellation_retained?: number;
  cancelled_by?: string;
  cancelled_at?: string;
}

const BookingDetail = () => {
  const match: any = useMatch("/booking-details/:id");
  const { authState, handleError } = useContext(GlobalContext);
  const [userDetails, setUserDetails] = useState<booking | null>(null);
  const [loading, setLoading] = useState(true);
  const [invoiceModal, setInvoiceModal] = useState(false);

  if (authState.access_token) {
    henceforthApi.setToken(authState.access_token);
  }

  const getBookingDetails = async () => {
    try {
      setLoading(true);
      const userDetail = await henceforthApi.BookingListing.getBookingDetails(
        match?.params?.id
      );
      if (userDetail?.data && userDetail.data.length > 0) {
        setUserDetails(userDetail.data[0]);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      handleError(error);
    }
  };

  useEffect(() => {
    if (match?.params?.id) {
      getBookingDetails();
    }
  }, [match?.params?.id]);

  const copyText = (text?: string, label: string = "ID") => {
    if (text) {
      navigator?.clipboard?.writeText(text);
      toast.success(`${label} copied to clipboard`);
    }
  };

  const formatCurrency = (amount: number | string | undefined | null) => {
    if (amount === undefined || amount === null) return "0.00";
    const num = Number(amount);
    const formatted = isNaN(num)
      ? "0.00"
      : num.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

    if (userDetails?.currency === henceofrthEnums.CurrencyType.NGN) {
      return `₦${formatted}`;
    }
    if (userDetails?.currency === henceofrthEnums.CurrencyType.INR) {
      return `₹${formatted}`;
    }
    return `$${formatted}`;
  };

  const handleReassignDriver = () => {
    Swal.fire({
      title: "Reassign Booking",
      text: "Do you want to reassign this booking to another available driver?",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#064e3b",
      cancelButtonColor: "#64748b",
      confirmButtonText: "Confirm",
    });
  };

  if (loading) {
    return (
      <div className="py-24 flex flex-col items-center justify-center text-slate-400">
        <div className="w-9 h-9 border-3 border-[#006747] border-t-transparent rounded-full animate-spin mb-3"></div>
        <p className="text-sm font-medium text-slate-500">
          Loading booking details...
        </p>
      </div>
    );
  }

  if (!userDetails) {
    return (
      <div className="py-20 text-center">
        <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-3" />
        <h4 className="text-lg font-bold text-slate-800">Booking Not Found</h4>
        <p className="text-xs text-slate-500 mt-1 mb-5">
          The requested booking details could not be retrieved.
        </p>
        <Button
          onClick={() => window.history.back()}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          <span>Go Back to Bookings</span>
        </Button>
      </div>
    );
  }

  // Safe checks for presence of fields in API response
  const fullBookingId = userDetails.booking_id || userDetails._id || "";
  const displayBookingId =
    userDetails.booking_id ||
    (userDetails._id
      ? `${userDetails._id.slice(0, 8)}...${userDetails._id.slice(-4)}`
      : "Not available");

  const hasRouteSchedule = Boolean(
    userDetails.pickup?.address ||
    userDetails.city_from ||
    userDetails.dropoff?.address ||
    userDetails.city_to ||
    userDetails.date ||
    userDetails.pickup_time ||
    userDetails.drop_off_time
  );

  const hasFares =
    userDetails.price !== undefined ||
    userDetails.total_price !== undefined ||
    userDetails.tax !== undefined ||
    userDetails.price_before_tax !== undefined;

  const hasPassenger = Boolean(
    userDetails.user_detail &&
    (userDetails.user_detail._id ||
      userDetails.user_detail.name ||
      userDetails.user_detail.email)
  );

  const hasDriver = Boolean(
    userDetails.driver_detail &&
    (userDetails.driver_detail._id ||
      userDetails.driver_detail.name ||
      userDetails.driver_detail.email)
  );

  const hasVehicle = Boolean(
    userDetails.vehicle_brand_name ||
    userDetails.licence_plate_number ||
    userDetails.model_of_car ||
    userDetails.vehicle_color_name
  );

  const recordingsList: any[] = Array.isArray(userDetails.recordings)
    ? userDetails.recordings
    : [];

  const statusRaw = (userDetails.satus || "CONFIRMED").toUpperCase().replaceAll("_", " ");
  const isConfirmed = statusRaw === "CONFIRMED" || statusRaw === "ACCEPTED";
  const isCompleted = statusRaw === "COMPLETED";
  const isCancelled = statusRaw === "CANCELLED" || statusRaw === "REJECTED";

  const totalFareValue =
    userDetails.total_price ?? userDetails.price ?? 0;
  const taxValue = userDetails.tax ?? 0;
  const baseFareValue =
    userDetails.price_before_tax !== undefined &&
      userDetails.price_before_tax !== null
      ? userDetails.price_before_tax
      : userDetails.tax !== undefined
        ? Number(totalFareValue) - Number(taxValue)
        : totalFareValue;

  const pickupLocation =
    userDetails.pickup?.address || userDetails.city_from || "Not available";
  const dropoffLocation =
    userDetails.dropoff?.address || userDetails.city_to || "Not available";

  const pickupDateFormatted = userDetails.date
    ? moment(Number(userDetails.date)).format("DD MMM YYYY")
    : null;

  return (
    <div className="w-full max-w-[1600px] mx-auto space-y-5 pb-12">
      {/* 1. SINGLE CLEAN BREADCRUMB NAVIGATION */}
      <nav className="flex items-center gap-1.5 text-xs text-slate-400 font-medium">
        <Link to="/" className="text-slate-500 hover:text-emerald-700 transition-colors">
          Dashboard
        </Link>
        <ChevronRight className="w-3.5 h-3.5 text-slate-300" />
        <Link
          to="/booked-rides/1"
          className="text-slate-500 hover:text-emerald-700 transition-colors"
        >
          Booked Rides
        </Link>
        <ChevronRight className="w-3.5 h-3.5 text-slate-300" />
        <span className="text-emerald-700 font-semibold">Booking Details</span>
      </nav>

      {/* 2. PAGE HEADER & QUICK ACTIONS */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-2 border-b border-slate-100">
        <div>
          <div className="flex flex-wrap items-center gap-2.5">
            <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
              Booking Details
            </h1>

            {/* Status Badge */}
            <span
              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide border ${isCompleted
                ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                : isConfirmed
                  ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                  : isCancelled
                    ? "bg-rose-50 text-rose-700 border-rose-200"
                    : "bg-amber-50 text-amber-700 border-amber-200"
                }`}
            >
              <span
                className={`w-2 h-2 rounded-full ${isCancelled
                  ? "bg-rose-500"
                  : isConfirmed || isCompleted
                    ? "bg-emerald-500"
                    : "bg-amber-500"
                  }`}
              />
              {statusRaw}
            </span>
          </div>

          <p className="text-xs text-slate-500 mt-1 mb-0">
            Booking ID:{" "}
            <span className="font-mono font-semibold text-slate-700">
              #{fullBookingId || "Not Available"}
            </span>
          </p>
        </div>
      </div>

      {/* 3. TOP SUMMARY METRIC CARDS (REAL FIELDS ONLY) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Booking ID */}
        <div className="bg-white rounded-2xl border border-slate-200/80 p-4 shadow-2xs">
          <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block mb-2">
            Booking ID
          </span>
          <div className="flex items-center justify-between gap-2">
            <span
              className="font-mono font-bold text-slate-800 text-sm truncate"
              title={fullBookingId}
            >
              {fullBookingId || "Not available"}
            </span>
          </div>
        </div>

        {/* Total Price */}
        <div className="bg-white rounded-2xl border border-slate-200/80 p-4 shadow-2xs">
          <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block mb-1">
            Total Price
          </span>
          <div className="text-2xl font-extrabold text-slate-900 tracking-tight">
            {formatCurrency(totalFareValue)}
          </div>
          {userDetails.tax !== undefined && (
            <p className="text-[11px] text-slate-400 mt-1 mb-0">
              Tax: {formatCurrency(taxValue)}
            </p>
          )}
        </div>

        {/* Pickup Schedule */}
        <div className="bg-white rounded-2xl border border-slate-200/80 p-4 shadow-2xs">
          <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block mb-1">
            Pickup Date
          </span>
          <div className="text-base font-bold text-slate-800">
            {pickupDateFormatted || "Not available"}
          </div>
          <p className="text-[11px] text-slate-500 mt-1 mb-0">
            Time:{" "}
            <strong className="text-slate-700">
              {userDetails.pickup_time || "Not available"}
            </strong>
          </p>
        </div>

        {/* Status */}
        <div className="bg-white rounded-2xl border border-slate-200/80 p-4 shadow-2xs">
          <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block mb-1">
            Ride Status
          </span>
          <div className="text-base font-bold text-slate-800">
            {statusRaw}
          </div>
          {userDetails.drop_off_time && (
            <p className="text-[11px] text-slate-500 mt-1 mb-0">
              Drop-Off Time:{" "}
              <strong className="text-slate-700">{userDetails.drop_off_time}</strong>
            </p>
          )}
        </div>
      </div>

      {/* 4. MAIN 2-COLUMN WORKSPACE (ONLY REAL DATA FIELDS) */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        {/* LEFT COLUMN: Route Details & Fare Breakdown */}
        <div className="lg:col-span-7 space-y-6">
          {/* Card L1: Route & Schedule */}
          {hasRouteSchedule && (
            <div className="bg-white rounded-2xl border border-slate-200/80 p-3 shadow-2xs">
              <div className="flex items-center justify-between pb-3 mb-4 border-b border-slate-100">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-700 flex items-center justify-center">
                    <Navigation className="w-4 h-4" />
                  </div>
                  <h3 className="text-sm sm:text-base font-bold text-slate-900 mb-0">
                    Route & Schedule
                  </h3>
                </div>
                {pickupDateFormatted && (
                  <span className="text-xs font-semibold text-slate-600 bg-slate-50 border border-slate-200/70 px-2.5 py-1 rounded-lg">
                    {pickupDateFormatted}
                  </span>
                )}
              </div>

              {/* Stops Timeline */}
              <div className="space-y-4 pl-1">
                {/* Pickup Location */}
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-emerald-100 border-2 border-emerald-500 flex items-center justify-center shrink-0 mt-0.5">
                    <div className="w-2 h-2 rounded-full bg-emerald-600" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                        Pick up Location
                      </span>
                      {userDetails.pickup_time && (
                        <span className="bg-slate-100 text-slate-700 text-[11px] font-semibold px-2 py-0.5 rounded">
                          {userDetails.pickup_time}
                        </span>
                      )}
                    </div>
                    <p className="text-xs font-semibold text-slate-800 mt-1 leading-relaxed">
                      {pickupLocation}
                    </p>
                    {userDetails.pickup?.lat && userDetails.pickup?.long && (
                      <span className="inline-block mt-1 bg-slate-50 text-slate-600 border border-slate-200 text-[10px] font-medium px-2 py-0.5 rounded">
                        Geo: {userDetails.pickup.lat.toFixed(4)}° N,{" "}
                        {userDetails.pickup.long.toFixed(4)}° E
                      </span>
                    )}
                  </div>
                </div>

                {/* Connecting Line */}
                <div className="ml-3 pl-3 border-1 border-dotted border-slate-200" />

                {/* Dropoff Location */}
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-rose-100 border-2 border-rose-500 flex items-center justify-center shrink-0 mt-0.5">
                    <MapPin className="w-3 h-3 text-rose-600" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                        Drop off Location
                      </span>
                      {userDetails.drop_off_time && (
                        <span className="bg-slate-100 text-slate-700 text-[11px] font-semibold px-2 py-0.5 rounded">
                          {userDetails.drop_off_time}
                        </span>
                      )}
                    </div>
                    <p className="text-xs font-semibold text-slate-800 mt-1 leading-relaxed">
                      {dropoffLocation}
                    </p>
                    {userDetails.dropoff?.lat && userDetails.dropoff?.long && (
                      <span className="inline-block mt-1 bg-slate-50 text-slate-600 border border-slate-200 text-[10px] font-medium px-2 py-0.5 rounded">
                        Geo: {userDetails.dropoff.lat.toFixed(4)}° N,{" "}
                        {userDetails.dropoff.long.toFixed(4)}° E
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Card L2: Fare Breakdown */}
          {hasFares && (
            <div className="bg-white rounded-2xl border border-slate-200/80 p-3 shadow-2xs">
              <div className="flex items-center justify-between pb-3 mb-4 border-b border-slate-100">
                <h3 className="text-sm sm:text-base font-bold text-slate-900 mb-0">
                  Fare Details
                </h3>
                <span className="text-xs font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 px-2.5 py-0.5 rounded-full">
                  {statusRaw}
                </span>
              </div>

              {/* Line Items */}
              <div className="space-y-3 text-xs">
                {baseFareValue !== undefined && (
                  <div className="flex items-center justify-between py-1">
                    <span className="text-slate-600 font-medium">Base Fare :</span>
                    <span className="font-mono font-bold text-slate-800 text-sm">
                      {formatCurrency(baseFareValue)}
                    </span>
                  </div>
                )}

                {userDetails.tax !== undefined && (
                  <div className="flex items-center justify-between py-1 border-t border-slate-100">
                    <span className="text-slate-600 font-medium">Tax :</span>
                    <span className="font-mono font-bold text-emerald-700 text-sm">
                      {formatCurrency(taxValue)}
                    </span>
                  </div>
                )}

                {/* Refund & Cancellation fields from old code */}
                {userDetails.refund_amount !== undefined &&
                  userDetails.refund_amount !== null &&
                  userDetails.refund_amount > 0 && (
                    <div className="flex items-center justify-between py-1.5 border-t border-rose-100 text-rose-700">
                      <span className="font-bold">
                        Refund Amount ({userDetails.refund_percentage || 0}%) :
                      </span>
                      <span className="font-mono font-bold text-sm">
                        -{formatCurrency(userDetails.refund_amount)}
                      </span>
                    </div>
                  )}

                {userDetails.driver_cancellation_payout !== undefined &&
                  userDetails.driver_cancellation_payout !== null &&
                  userDetails.driver_cancellation_payout > 0 && (
                    <div className="flex items-center justify-between py-1 border-t border-slate-100 text-slate-600">
                      <span className="font-medium">Driver Cancellation Payout :</span>
                      <span className="font-mono font-semibold">
                        {formatCurrency(userDetails.driver_cancellation_payout)}
                      </span>
                    </div>
                  )}

                {userDetails.admin_cancellation_retained !== undefined &&
                  userDetails.admin_cancellation_retained !== null &&
                  userDetails.admin_cancellation_retained > 0 && (
                    <div className="flex items-center justify-between py-1 border-t border-slate-100 text-slate-600">
                      <span className="font-medium">Admin Retained Fee + Tax :</span>
                      <span className="font-mono font-semibold">
                        {formatCurrency(userDetails.admin_cancellation_retained)}
                      </span>
                    </div>
                  )}

                {/* Total Price Highlight */}
                <div className="flex items-center justify-between pt-3 mt-2 border-t-2 border-slate-100">
                  <span className="text-xs font-bold text-slate-900 uppercase tracking-wider">
                    Total Price :
                  </span>
                  <span className="text-xl font-extrabold text-[#006747]">
                    {formatCurrency(totalFareValue)}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* RIGHT COLUMN: Passenger, Driver & Vehicle, Recordings */}
        <div className="lg:col-span-5 space-y-6">
          {/* Card R1: Passenger Details */}
          {hasPassenger && (
            <div className="bg-white rounded-2xl border border-slate-200/80 p-3 shadow-2xs">
              <div className="flex items-center justify-between pb-3 mb-3 border-b border-slate-100">
                <span className="text-xs font-bold text-slate-800 uppercase tracking-wider">
                  Passenger Details
                </span>
                <ShieldCheck className="w-4 h-4 text-emerald-600" />
              </div>

              <div className="flex items-center gap-3">
                <img
                  src={
                    userDetails.user_detail?.profile_pic &&
                      userDetails.user_detail.profile_pic !== "null"
                      ? `${henceforthApi.API_FILE_ROOT_ORIGINAL}${userDetails.user_detail.profile_pic}`
                      : profile_placeholder
                  }
                  alt={userDetails.user_detail?.name || "Passenger"}
                  className="w-12 h-12 rounded-full object-cover border border-slate-200 shrink-0"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = profile_placeholder;
                  }}
                />

                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-bold text-slate-900 truncate mb-0">
                    {userDetails.user_detail?.name || "Not Available"}
                  </h4>
                  <p className="text-xs text-slate-500 truncate mt-0.5 mb-1">
                    {userDetails.user_detail?.email || "Not available"}
                  </p>
                  {userDetails.user_detail?._id && (
                    <div className="flex items-center gap-1.5 text-[11px] text-slate-400 font-mono">
                      <span className="truncate max-w-[170px]">
                        ID: {userDetails.user_detail._id}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Passenger Ratings & Reviews */}
              <div className="grid grid-cols-2 gap-2 bg-slate-50 border border-slate-100 rounded-xl p-3 my-3 text-center">
                <div>
                  <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider block">
                    Rating
                  </span>
                  <div className="flex items-center justify-center gap-1 mt-0.5">
                    <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                    <span className="text-sm font-bold text-slate-800">
                      {userDetails.user_detail?.average_ratings !== undefined
                        ? userDetails.user_detail.average_ratings
                        : "0.0"}
                    </span>
                  </div>
                </div>
                <div>
                  <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider block">
                    Reviews
                  </span>
                  <span className="text-sm font-bold text-slate-800 mt-0.5 block">
                    {userDetails.user_detail?.total_reviews ?? 0}
                  </span>
                </div>
              </div>

              {/* Link to Passenger Profile */}
              {userDetails.user_detail?._id && (
                <div className="pt-1 text-right">
                  <Link
                    to={`/user/${userDetails.user_detail._id}`}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 hover:underline"
                  >
                    <span>View User Profile</span>
                    <ExternalLink className="w-3 h-3" />
                  </Link>
                </div>
              )}
            </div>
          )}

          {/* Card R2: Driver & Vehicle Details */}
          {hasDriver && (
            <div className="bg-white rounded-2xl border border-slate-200/80 p-3 shadow-2xs">
              <div className="flex items-center justify-between pb-3 mb-3 border-b border-slate-100">
                <span className="text-xs font-bold text-slate-800 uppercase tracking-wider">
                  Driver & Vehicle Detail
                </span>
                <span className="text-xs text-slate-400">Assigned Driver</span>
              </div>

              {/* Driver Info */}
              <div className="flex items-center gap-3">
                <img
                  src={
                    userDetails.driver_detail?.profile_pic &&
                      userDetails.driver_detail.profile_pic !== "null"
                      ? `${henceforthApi.API_FILE_ROOT_ORIGINAL}${userDetails.driver_detail.profile_pic}`
                      : profile_placeholder
                  }
                  alt={userDetails.driver_detail?.name || "Driver"}
                  className="w-12 h-12 rounded-full object-cover border border-slate-200 shrink-0"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = profile_placeholder;
                  }}
                />

                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-bold text-slate-900 truncate mb-0">
                    {userDetails.driver_detail?.name || "Not Available"}
                  </h4>
                  <p className="text-xs text-slate-500 truncate mt-0.5 mb-1">
                    {userDetails.driver_detail?.email || "Not available"}
                  </p>
                  {userDetails.driver_detail?._id && (
                    <div className="flex items-center gap-1.5 text-[11px] text-slate-400 font-mono">
                      <span className="truncate max-w-[170px]">
                        ID: {userDetails.driver_detail._id}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Driver Ratings & Reviews */}
              <div className="grid grid-cols-2 gap-2 bg-slate-50 border border-slate-100 rounded-xl p-3 my-3 text-center">
                <div>
                  <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider block">
                    Rating
                  </span>
                  <div className="flex items-center justify-center gap-1 mt-0.5">
                    <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                    <span className="text-sm font-bold text-slate-800">
                      {userDetails.driver_detail?.average_ratings !== undefined
                        ? userDetails.driver_detail.average_ratings
                        : "0.0"}
                    </span>
                  </div>
                </div>
                <div>
                  <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider block">
                    Reviews
                  </span>
                  <span className="text-sm font-bold text-slate-800 mt-0.5 block">
                    {userDetails.driver_detail?.total_reviews ?? 0}
                  </span>
                </div>
              </div>

              {/* Vehicle Detail (Old Fields) */}
              {hasVehicle && (
                <div className="mt-3.5 bg-slate-50 border border-slate-200/80 rounded-xl p-3 space-y-1.5 text-xs">
                  <div className="flex items-center gap-2 pb-1 border-b border-slate-200/60 font-bold text-slate-800">
                    <Car className="w-3.5 h-3.5 text-slate-600" />
                    <span>Vehicle Detail</span>
                  </div>

                  <div className="flex justify-between py-0.5">
                    <span className="text-slate-500">Name :</span>
                    <span className="font-semibold text-slate-800">
                      {userDetails.vehicle_brand_name || "Not Available"}
                    </span>
                  </div>

                  <div className="flex justify-between py-0.5">
                    <span className="text-slate-500">Number :</span>
                    <span className="font-mono font-semibold text-slate-800">
                      {userDetails.licence_plate_number || "Not available"}
                    </span>
                  </div>

                  <div className="flex justify-between py-0.5">
                    <span className="text-slate-500">Model :</span>
                    <span className="font-semibold text-slate-800">
                      {userDetails.model_of_car || "Not available"}
                    </span>
                  </div>

                  <div className="flex justify-between py-0.5">
                    <span className="text-slate-500">Color :</span>
                    <span className="font-semibold text-slate-800">
                      {userDetails.vehicle_color_name || "Not available"}
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Card R3: Recordings */}
          <div className="bg-white rounded-2xl border border-slate-200/80 p-3 shadow-2xs">
            <div className="flex items-center justify-between pb-3 mb-3 border-b border-slate-100">
              <span className="text-xs font-bold text-slate-800 uppercase tracking-wider">
                Recordings
              </span>
              <span className="text-xs text-slate-400 font-medium">
                {recordingsList.length} files
              </span>
            </div>

            {recordingsList.length > 0 ? (
              <div className="space-y-3">
                {recordingsList.map((item: any, idx: number) => {
                  if (item?.type === "AUDIO") {
                    return (
                      <div
                        key={idx}
                        className="p-3 rounded-xl bg-slate-50 border border-slate-200"
                      >
                        <p className="text-xs font-semibold text-slate-700 mb-2">
                          Audio Recording {idx + 1}
                        </p>
                        <audio
                          controls
                          src={`${henceforthApi?.API_FILE_ROOT_AUDIO}${item.url}`}
                          className="w-full h-8"
                        />
                      </div>
                    );
                  }
                  return (
                    <div
                      key={idx}
                      className="p-3 rounded-xl bg-slate-50 border border-slate-200"
                    >
                      <video
                        controls
                        src={`${henceforthApi?.API_FILE_ROOT_VIDEO}${item?.url}`}
                        className="w-full max-h-60 rounded-lg object-contain bg-black"
                      />
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="bg-[#f8fafc] rounded-2xl p-6 text-center border border-slate-100">
                <div className="w-10 h-10 rounded-full bg-slate-100 text-slate-400 flex items-center justify-center mx-auto mb-2">
                  <MicOff className="w-5 h-5" />
                </div>
                <h5 className="text-sm font-bold text-slate-800 mb-1">
                  No Recording Found
                </h5>
                <p className="text-xs text-slate-400 mb-0">
                  No audio or video recordings were captured for this ride.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Invoice Modal Preview */}
      {invoiceModal && (
        <div
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4"
          onClick={() => setInvoiceModal(false)}
        >
          <div
            className="bg-white rounded-2xl max-w-md w-full p-6 overflow-hidden relative shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between pb-3 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-[#006747]" />
                <span className="text-sm font-bold text-slate-800">
                  Trip Invoice
                </span>
              </div>
              <Button
                type="button"
                onClick={() => setInvoiceModal(false)}
                className="text-slate-400 hover:text-slate-700 text-sm font-bold p-1 cursor-pointer"
              >
                ✕
              </Button>
            </div>

            <div className="py-4 space-y-2.5 text-xs">
              <div className="flex justify-between py-1 border-b border-slate-50">
                <span className="text-slate-500">Booking Reference</span>
                <span className="font-mono font-semibold text-slate-800">
                  #{fullBookingId}
                </span>
              </div>
              {pickupDateFormatted && (
                <div className="flex justify-between py-1 border-b border-slate-50">
                  <span className="text-slate-500">Date</span>
                  <span className="font-semibold text-slate-800">
                    {pickupDateFormatted}
                  </span>
                </div>
              )}
              {userDetails.user_detail?.name && (
                <div className="flex justify-between py-1 border-b border-slate-50">
                  <span className="text-slate-500">Passenger</span>
                  <span className="font-semibold text-slate-800">
                    {userDetails.user_detail.name}
                  </span>
                </div>
              )}
              {userDetails.driver_detail?.name && (
                <div className="flex justify-between py-1 border-b border-slate-50">
                  <span className="text-slate-500">Driver</span>
                  <span className="font-semibold text-slate-800">
                    {userDetails.driver_detail.name}
                  </span>
                </div>
              )}
              {baseFareValue !== undefined && (
                <div className="flex justify-between py-1 border-b border-slate-50">
                  <span className="text-slate-500">Base Fare</span>
                  <span className="font-semibold text-slate-800">
                    {formatCurrency(baseFareValue)}
                  </span>
                </div>
              )}
              {userDetails.tax !== undefined && (
                <div className="flex justify-between py-1 border-b border-slate-50">
                  <span className="text-slate-500">Tax</span>
                  <span className="font-semibold text-slate-800">
                    {formatCurrency(taxValue)}
                  </span>
                </div>
              )}
              <div className="flex justify-between pt-3 text-base font-bold text-slate-900 border-t border-slate-200">
                <span>Total Price</span>
                <span className="text-[#006747]">
                  {formatCurrency(totalFareValue)}
                </span>
              </div>
            </div>

            <div className="flex items-center gap-2 pt-3">
              <Button
                type="button"
                onClick={() => {
                  window.print();
                }}
                className="flex-1 py-2.5 rounded-xl text-xs font-semibold bg-[#064e3b] hover:bg-[#043d2e] text-white transition-colors cursor-pointer"
              >
                Print Invoice
              </Button>
              <Button
                type="button"
                onClick={() => setInvoiceModal(false)}
                className="py-2.5 px-4 rounded-xl text-xs font-semibold bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors cursor-pointer"
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingDetail;